import { useEffect, useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, DownloadSimple, ArrowSquareOut, FilePdf } from "@phosphor-icons/react";

/**
 * Universeller PDF-Viewer-Dialog.
 * Funktioniert auf Desktop, Mobile und als installierte PWA — der Nutzer hat
 * IMMER einen großen Schließen-Button (vorher: window.open hat in der PWA
 * eine WebView ohne Browser-Chrome geöffnet → kein X zum Schließen).
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   url: string (vollständige PDF-URL)
 *   title?: string
 *   filename?: string (für Download)
 */
export default function PdfViewerDialog({ open, onClose, url, title = "PDF-Vorschau", filename }) {
  // Fehlererkennung: iOS Safari rendert PDFs in iFrames oft nicht inline.
  const [iframeOk, setIframeOk] = useState(true);
  const isIOS = typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent || "") &&
    !window.MSStream;

  useEffect(() => {
    if (open) setIframeOk(true);
  }, [open, url]);

  if (!url) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent
        className="max-w-5xl w-[96vw] h-[92vh] p-0 flex flex-col overflow-hidden"
        data-testid="pdf-viewer-dialog"
      >
        {/* HEADER mit großem Schließen-Button */}
        <div className="flex items-center justify-between px-3 md:px-4 py-2.5 border-b bg-slate-50 flex-shrink-0 gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <FilePdf size={20} weight="duotone" className="text-rose-600 flex-shrink-0" />
            <div className="font-semibold truncate text-sm md:text-base">{title}</div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {/* Download — funktioniert auf iOS zuverlässig */}
            <a
              href={url}
              download={filename || "dokument.pdf"}
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-xs font-medium text-slate-700"
              data-testid="pdf-download-link"
            >
              <DownloadSimple size={14} /> Herunterladen
            </a>
            {/* Nur Icon-Version für Mobile */}
            <a
              href={url}
              download={filename || "dokument.pdf"}
              className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-700"
              aria-label="Herunterladen"
              data-testid="pdf-download-link-mobile"
            >
              <DownloadSimple size={16} />
            </a>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-xs font-medium text-slate-700"
              data-testid="pdf-open-new-tab"
            >
              <ArrowSquareOut size={14} /> Neuer Tab
            </a>
            <Button
              onClick={onClose}
              size="sm"
              className="h-9 px-3 bg-rose-600 hover:bg-rose-700 text-white gap-1.5"
              data-testid="pdf-close-btn"
            >
              <X size={16} weight="bold" />
              <span className="hidden sm:inline">Schließen</span>
            </Button>
          </div>
        </div>

        {/* BODY */}
        <div className="flex-1 bg-slate-100 overflow-hidden relative">
          {isIOS || !iframeOk ? (
            <div className="h-full flex flex-col items-center justify-center p-6 text-center gap-4">
              <FilePdf size={64} weight="duotone" className="text-rose-500" />
              <div>
                <p className="font-semibold text-slate-800">PDF kann auf diesem Gerät nicht direkt angezeigt werden</p>
                <p className="text-sm text-slate-500 mt-1">Bitte lade das PDF herunter oder öffne es in einem neuen Tab.</p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
                <a
                  href={url}
                  download={filename || "dokument.pdf"}
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                  data-testid="pdf-fallback-download"
                >
                  <DownloadSimple size={18} /> PDF herunterladen
                </a>
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-semibold"
                  data-testid="pdf-fallback-newtab"
                >
                  <ArrowSquareOut size={18} /> In neuem Tab öffnen
                </a>
              </div>
            </div>
          ) : (
            <iframe
              src={url}
              title={title}
              className="w-full h-full border-0"
              onError={() => setIframeOk(false)}
              data-testid="pdf-iframe"
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
